import React, { useEffect, useState } from "react";
import { consent, setConsent } from "@veyso/core";
import { analyticsConfig } from "../auth/config.js";

/**
 * The portal's own cookie prompt.
 *
 * It has one because it has to. The marketing site asks the same question, but
 * the two are different origins — veyso-site.fly.dev and veyso-web.fly.dev
 * today, veyso.io and app.veyso.io later — and `localStorage` does not cross an
 * origin, subdomains included. An answer given on the site is not readable
 * here, and treating it as given would be asserting consent the browser never
 * reported.
 *
 * Both answers are the same button, deliberately. A prompt whose accept is
 * filled and whose decline is grey small print has decided for you.
 *
 * Not shown at all when there is no analytics configured, which is every
 * self-hosted install and every laptop: a question about a thing that will not
 * happen either way is noise.
 */
export const CookieBanner: React.FC = () => {
  // After mount, because localStorage is per-browser state and reading it while
  // rendering makes the first paint depend on it.
  const [asked, setAsked] = useState(true);

  useEffect(() => {
    if (!analyticsConfig()) return;
    setAsked(consent() !== null);
  }, []);

  if (asked) return null;

  const answer = (value: "accepted" | "declined") => {
    setConsent(value);
    setAsked(true);
  };

  return (
    <aside role="region" aria-label="Cookies" className="fixed inset-x-0 bottom-0 z-40 flex justify-center p-3">
      <div className="flex w-full max-w-[640px] flex-wrap items-center gap-3 rounded-[10px] border border-bay-700 bg-bay-850 px-4 py-3 shadow-lg">
        <p className="min-w-[16rem] flex-1 text-[12px] leading-[1.5] text-ink-300">
          {
            "We would like to count how the app is used, so we can see which parts help and which do not. Nothing is loaded until you say. "
          }
          <a
            className="whitespace-nowrap text-ink-100 underline"
            href="https://veyso.io/cookies"
            target="_blank"
            rel="noreferrer"
          >
            {"What this means"}
          </a>
        </p>
        <div className="ml-auto flex gap-2">
          {/* Variants, not bare `.btn` — which is layout only and renders as
              plain text. A consent banner whose two answers do not look like
              buttons is the worst place in the app to have that. */}
          <button className="btn btn-secondary" onClick={() => answer("declined")}>
            {"No thanks"}
          </button>
          <button className="btn btn-primary" onClick={() => answer("accepted")}>
            {"That is fine"}
          </button>
        </div>
      </div>
    </aside>
  );
};
